import { useEffect, type ReactNode } from 'react';
import { weddingTheme, type OklchColor } from '@/data/weddingTheme';

function toCss([l, c, h]: OklchColor): string {
  return `${l} ${c} ${h}`;
}

function adjust([l, c, h]: OklchColor, dl: number, dc = 0, dh = 0): OklchColor {
  return [
    Math.max(0, Math.min(1, l + dl)),
    Math.max(0, c + dc),
    h + dh,
  ];
}

interface ThemeProviderProps {
  children: ReactNode;
}

/**
 * Applies the central theme (colors + fonts) from `weddingTheme.ts`
 * to the global CSS variables on mount. Editing `weddingTheme.ts`
 * is all that is required to restyle the entire invitation.
 */
export default function ThemeProvider({ children }: ThemeProviderProps) {
  useEffect(() => {
    const root = document.documentElement.style;
    const { fonts, colors } = weddingTheme;
    const set = (name: string, value: string) => root.setProperty(name, value);
    const setColor = (name: string, value: OklchColor) => set(name, toCss(value));

    // Typography
    set('--font-heading', fonts.title);
    set('--font-body', fonts.body);
    set('--font-label', fonts.body);
    set('--font-script', fonts.accent);

    const {
      background,
      backgroundAlt,
      text,
      accent,
      muted,
      dark,
      buttonBackground,
      buttonText,
    } = colors;

    // Background scale
    setColor('--background-50', background);
    setColor('--background-100', backgroundAlt);
    setColor('--background-200', adjust(backgroundAlt, -0.05, 0.005, -5));
    setColor('--background-300', adjust(backgroundAlt, -0.1, 0.01, -8));
    setColor('--background-400', adjust(backgroundAlt, -0.2, 0.015, -15));
    setColor('--background-500', adjust(backgroundAlt, -0.3, 0.02, -20));
    setColor('--background-600', adjust(backgroundAlt, -0.4, 0.02, -22));
    setColor('--background-700', adjust(backgroundAlt, -0.52, 0.02, -24));
    setColor('--background-800', adjust(backgroundAlt, -0.64, 0.018, -26));
    setColor('--background-900', adjust(dark, 0.07));
    setColor('--background-950', dark);

    // Foreground (text) scale
    setColor('--foreground-50', background);
    setColor('--foreground-200', adjust(muted, 0.35));
    setColor('--foreground-300', adjust(muted, 0.25));
    setColor('--foreground-400', adjust(muted, 0.15));
    setColor('--foreground-500', muted);
    setColor('--foreground-600', adjust(text, 0.25));
    setColor('--foreground-700', adjust(text, 0.15));
    setColor('--foreground-800', adjust(text, 0.05));
    setColor('--foreground-900', text);
    setColor('--foreground-950', adjust(text, -0.05));

    // Primary (accent) scale
    setColor('--primary-100', adjust(accent, 0.22));
    setColor('--primary-200', adjust(accent, 0.18));
    setColor('--primary-300', adjust(accent, 0.12));
    setColor('--primary-400', adjust(accent, 0.06));
    setColor('--primary-500', buttonBackground);
    setColor('--primary-600', adjust(buttonBackground, -0.1));
    setColor('--primary-700', adjust(buttonBackground, -0.2));

    // Accent (sage/olive) scale derived from the accent hue for subtle variety
    setColor('--accent-500', adjust(accent, 0.0, -0.03, 45));

    // Secondary (taupe) scale
    setColor('--secondary-500', muted);
    setColor('--secondary-600', adjust(muted, -0.1));

    // Expose raw semantic colors for advanced customization
    set('--theme-background', toCss(background));
    set('--theme-background-alt', toCss(backgroundAlt));
    set('--theme-text', toCss(text));
    set('--theme-accent', toCss(accent));
    set('--theme-muted', toCss(muted));
    set('--theme-dark', toCss(dark));
    set('--theme-button-background', toCss(buttonBackground));
    set('--theme-button-text', toCss(buttonText));
  }, []);

  return <>{children}</>;
}